import { motion } from "framer-motion";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const badgeStyles = {
  HOT: "bg-voltaic-pink text-white",
  NEW: "bg-voltaic-cyan text-voltaic-bg",
  LIMITED: "bg-voltaic-amber text-voltaic-bg",
};

export default function ProductCard({ product, onSelect, index = 0 }) {
  const { addItem } = useCart();

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const quickAdd = (e) => {
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMouseMove}
      onClick={() => onSelect(product)}
      className="card-spotlight group relative cursor-pointer overflow-hidden rounded-2xl border border-voltaic-line bg-voltaic-surface"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-voltaic-bg/60 via-transparent to-transparent" />
        {product.badge && (
          <span
            className={`absolute left-3 top-3 rounded-full px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] ${badgeStyles[product.badge] || "bg-voltaic-cyan text-voltaic-bg"}`}
          >
            {product.badge}
          </span>
        )}
        <button
          data-testid={`quick-add-${product.id}`}
          onClick={quickAdd}
          aria-label={`Quick add ${product.name}`}
          className="absolute bottom-3 right-3 grid h-10 w-10 translate-y-2 place-items-center rounded-full bg-voltaic-cyan text-voltaic-bg opacity-0 transition-all duration-300 hover:glow-cyan group-hover:translate-y-0 group-hover:opacity-100"
        >
          <Plus size={18} />
        </button>
      </div>
      <div className="relative p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-voltaic-cyan">{product.category}</p>
        <h3 className="mt-1 font-display text-lg font-semibold leading-tight">{product.name}</h3>
        <div className="mt-2.5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-bold">{money(product.price)}</span>
            {product.compare_at && (
              <span className="text-sm text-voltaic-muted line-through">{money(product.compare_at)}</span>
            )}
          </div>
          <span className="flex items-center gap-1 font-mono text-xs text-voltaic-muted">
            <Star size={12} className="fill-voltaic-amber text-voltaic-amber" />
            {product.rating.toFixed(1)}
          </span>
        </div>
      </div>
    </motion.article>
  );
}
